import React, { useMemo } from "react";
import { BirdieGameGroup } from "@/types";
import { cn } from "@/lib/utils";

interface RecurringYearTabsProps {
  instances: BirdieGameGroup[];
  currentGameId: string;
  isAllTime: boolean;
  onToggleAllTime: () => void;
  onSelectInstance: (gameId: string) => void;
}

const RecurringYearTabs: React.FC<RecurringYearTabsProps> = ({
  instances,
  currentGameId,
  isAllTime,
  onToggleAllTime,
  onSelectInstance,
}) => {
  // Deduplicate instances by year — prefer the one matching currentGameId
  const dedupedInstances = useMemo(() => {
    const byYear = new Map<number, BirdieGameGroup>();
    for (const instance of instances) {
      const existing = byYear.get(instance.year);
      if (!existing || instance.id === currentGameId) {
        byYear.set(instance.year, instance);
      }
    }
    return [...byYear.values()].sort((a, b) => b.year - a.year);
  }, [instances, currentGameId]);

  return (
    <div className="mb-4 overflow-x-auto">
      <div className="flex gap-2 min-w-max pb-1">
        {/* All-Time tab */}
        <button
          onClick={onToggleAllTime}
          className={cn(
            "px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
            isAllTime
              ? "bg-forest text-white"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
        >
          All-Time
        </button>

        {/* Year tabs */}
        {dedupedInstances.map((instance) => (
          <button
            key={instance.id}
            onClick={() => {
              if (instance.id !== currentGameId) {
                onSelectInstance(instance.id);
              } else if (isAllTime) {
                onToggleAllTime();
              }
            }}
            className={cn(
              "px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
              !isAllTime && instance.id === currentGameId
                ? "bg-forest text-white"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {instance.year}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RecurringYearTabs;
